import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  CalculatorIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { InvoiceService } from '../../services/invoice.service';
import {
  useGSTR1Report,
  useGSTR3BReport,
  useInvoicesPage,
  useReportingGSTINs,
} from '../../hooks/useInvoices';
import { GSTR1SectionKey, TaxAmounts } from '../../types/gst-report.types';
import { INDIAN_STATES } from '../../types/customer.types';
import { GST_TYPE_LABELS, INVOICE_TYPE_LABELS } from '../../components/invoices/InvoiceForm';
import { INVOICE_STATUS_META, formatInvoiceDate, getEffectiveStatus } from '../../components/invoices/invoiceDisplay';
import { Pagination } from '../../components/ui/Pagination';
import {
  Field,
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type ReportTab = 'gstr1' | 'gstr3b';
type PeriodPreset = 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'this_year' | 'custom';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  last_quarter: 'Last quarter',
  this_year: 'This financial year',
  custom: 'Custom dates',
};

const SECTION_LABELS: Record<GSTR1SectionKey, { label: string; hint: string }> = {
  b2b: { label: 'B2B', hint: 'Invoices to customers with a GST number' },
  b2cl: { label: 'B2C large', hint: 'Other-state invoices above ₹1,00,000 to unregistered customers' },
  b2cs: { label: 'B2C small', hint: 'All other sales to unregistered customers' },
  exp: { label: 'Exports', hint: 'Export invoices, zero rated' },
  notes: { label: 'Credit and debit notes', hint: 'Notes issued to customers with a GST number' },
  nil: { label: 'Nil rated and exempt', hint: 'Bills of supply' },
};

const toDateInput = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// First and last day of a preset period. The financial year runs from April to March.
const periodDates = (preset: PeriodPreset): { from: string; to: string } => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const range = (fromYear: number, fromMonth: number, months: number) => ({
    from: toDateInput(new Date(fromYear, fromMonth, 1)),
    to: toDateInput(new Date(fromYear, fromMonth + months, 0)),
  });
  const quarterStart = month - (month % 3);

  switch (preset) {
    case 'last_month':
      return range(year, month - 1, 1);
    case 'this_quarter':
      return range(year, quarterStart, 3);
    case 'last_quarter':
      return range(year, quarterStart - 3, 3);
    case 'this_year':
      return range(month >= 3 ? year : year - 1, 3, 12);
    case 'this_month':
    default:
      return range(year, month, 1);
  }
};

const stateName = (code: string) => INDIAN_STATES.find((state) => state.gstCode === code)?.name || code;

// MMYYYY as a month name
const formatFilingPeriod = (fp: string) => {
  const month = Number(fp.slice(0, 2));
  const year = Number(fp.slice(2));
  if (!month || !year) return fp;
  return new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const tdClass = 'whitespace-nowrap px-4 py-3 text-sm text-gray-700';
const numClass = `${tdClass} text-right tabular-nums`;

// A titled card around one table of a return
const ReportCard: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
    <div className="border-b border-gray-200 px-4 py-3">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </section>
);

const EmptyRow: React.FC<{ colSpan: number; text: string }> = ({ colSpan, text }) => (
  <tr>
    <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={colSpan}>
      {text}
    </td>
  </tr>
);

const GSTReports: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ReportTab>('gstr1');
  const [gstin, setGstin] = useState('');
  const [preset, setPreset] = useState<PeriodPreset>('this_month');
  const [fromDate, setFromDate] = useState(() => periodDates('this_month').from);
  const [toDate, setToDate] = useState(() => periodDates('this_month').to);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const { data: gstins = [], isLoading: gstinsLoading } = useReportingGSTINs();

  // Start on the GST number most invoices were raised under
  useEffect(() => {
    if (!gstin && gstins.length > 0) setGstin(gstins[0].gstin);
  }, [gstins, gstin]);

  const gstinError = gstin && !GSTIN_PATTERN.test(gstin) ? 'A GST number has 15 characters, like 27ABCDE1234F1Z5' : '';
  const dateError = fromDate && toDate && toDate < fromDate ? 'The "to" date cannot be before the "from" date' : '';
  const params = useMemo(
    () => (gstin && !gstinError && fromDate && toDate && !dateError ? { gstin, fromDate, toDate } : null),
    [gstin, gstinError, fromDate, toDate, dateError],
  );

  const gstr1 = useGSTR1Report(activeTab === 'gstr1' ? params : null);
  const gstr3b = useGSTR3BReport(activeTab === 'gstr3b' ? params : null);
  const active = activeTab === 'gstr1' ? gstr1 : gstr3b;

  // The invoices behind the return, one page at a time
  const register = useInvoicesPage({
    page,
    limit: pageSize,
    supplierGSTIN: params?.gstin || 'none',
    reportable: true,
    dateFrom: params?.fromDate,
    dateTo: params?.toDate,
  });
  const registerRows = params ? register.data?.data ?? [] : [];
  const registerTotal = params ? register.data?.total ?? 0 : 0;
  const registerPages = register.data?.totalPages ?? 1;

  useEffect(() => {
    setPage(1);
  }, [gstin, fromDate, toDate]);

  const choosePreset = (next: PeriodPreset) => {
    setPreset(next);
    if (next !== 'custom') {
      const dates = periodDates(next);
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };

  const handleExport = async () => {
    if (!params) {
      toast.error('Choose a GST number and a period first');
      return;
    }
    setIsExporting(true);
    try {
      const blob =
        activeTab === 'gstr1'
          ? await InvoiceService.exportGSTR1Report(params.gstin, params.fromDate, params.toDate)
          : await InvoiceService.exportGSTR3BReport(params.gstin, params.fromDate, params.toDate);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${activeTab.toUpperCase()}_${params.gstin}_${params.fromDate}_${params.toDate}.json`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      toast.success('Report exported');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not export the report'));
    } finally {
      setIsExporting(false);
    }
  };

  const selectedGstin = gstins.find((entry) => entry.gstin === gstin);
  const statCardClass = 'flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200';

  const renderStats = (
    stats: { label: string; value: string; hint: string; icon: React.ElementType; tone: string }[],
  ) => (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className={statCardClass}>
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
            <stat.icon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-gray-500">{stat.label}</p>
            <p className="truncate text-xl font-semibold text-gray-900">{stat.value}</p>
            <p className="truncate text-xs text-gray-500">{stat.hint}</p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderGSTR1 = () => {
    const report = gstr1.data;
    if (!report) return null;
    const { summary } = report;
    const sectionKeys = Object.keys(SECTION_LABELS) as GSTR1SectionKey[];

    return (
      <div className="space-y-6">
        {renderStats([
          {
            label: 'Documents Reported',
            value: formatNumber(summary.documents),
            hint: `${formatNumber(summary.cancelled)} cancelled, not reported`,
            icon: DocumentTextIcon,
            tone: 'text-indigo-600 bg-indigo-50',
          },
          {
            label: 'Taxable Value',
            value: formatMoney(summary.taxableValue),
            hint: `Filing period ${formatFilingPeriod(report.fp)}`,
            icon: BanknotesIcon,
            tone: 'text-blue-600 bg-blue-50',
          },
          {
            label: 'Total Tax',
            value: formatMoney(summary.totalTax),
            hint: `IGST ${formatMoney(summary.igst)} · CGST ${formatMoney(summary.cgst)} · SGST ${formatMoney(summary.sgst)}`,
            icon: ReceiptPercentIcon,
            tone: 'text-teal-600 bg-teal-50',
          },
          {
            label: 'Invoice Value',
            value: formatMoney(summary.invoiceValue),
            hint: 'Taxable value plus tax, after round off',
            icon: CalculatorIcon,
            tone: 'text-green-600 bg-green-50',
          },
        ])}

        <ReportCard title="Sections of the return" description="Where each document of the period is reported.">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Section</th>
                <th className={`${thClass} text-right`}>Documents</th>
                <th className={`${thClass} text-right`}>Taxable Value</th>
                <th className={`${thClass} text-right`}>Tax</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {sectionKeys.map((key) => (
                <tr key={key} className={summary.sections[key].count === 0 ? 'text-gray-400' : ''}>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{SECTION_LABELS[key].label}</div>
                    <div className="text-xs text-gray-500">{SECTION_LABELS[key].hint}</div>
                  </td>
                  <td className={numClass}>{formatNumber(summary.sections[key].count)}</td>
                  <td className={numClass}>{formatMoney(summary.sections[key].taxableValue)}</td>
                  <td className={numClass}>{formatMoney(summary.sections[key].tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportCard>

        <ReportCard title="B2B: invoices by customer" description="One row per customer GST number.">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Customer GST Number</th>
                <th className={thClass}>State</th>
                <th className={`${thClass} text-right`}>Invoices</th>
                <th className={`${thClass} text-right`}>Taxable Value</th>
                <th className={`${thClass} text-right`}>Tax</th>
                <th className={`${thClass} text-right`}>Invoice Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {report.b2b.length === 0 ? (
                <EmptyRow colSpan={6} text="No invoices to registered customers in this period" />
              ) : (
                report.b2b.map((entry) => {
                  const lines = entry.inv.flatMap((inv) => inv.itms.map((item) => item.itm_det));
                  const taxable = lines.reduce((sum, line) => sum + line.txval, 0);
                  const tax = lines.reduce((sum, line) => sum + line.iamt + line.camt + line.samt + line.csamt, 0);
                  return (
                    <tr key={entry.ctin}>
                      <td className={`${tdClass} font-mono text-xs`}>{entry.ctin}</td>
                      <td className={tdClass}>{stateName(entry.ctin.slice(0, 2))}</td>
                      <td className={numClass}>{formatNumber(entry.inv.length)}</td>
                      <td className={numClass}>{formatMoney(taxable)}</td>
                      <td className={numClass}>{formatMoney(tax)}</td>
                      <td className={`${numClass} font-medium text-gray-900`}>
                        {formatMoney(entry.inv.reduce((sum, inv) => sum + inv.val, 0))}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </ReportCard>

        {report.b2cs.length > 0 && (
          <ReportCard title="B2C small: by state and rate" description="Sales to unregistered customers are added up, not listed one by one.">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>Place of Supply</th>
                  <th className={thClass}>Supply</th>
                  <th className={`${thClass} text-right`}>Rate</th>
                  <th className={`${thClass} text-right`}>Taxable Value</th>
                  <th className={`${thClass} text-right`}>IGST</th>
                  <th className={`${thClass} text-right`}>CGST</th>
                  <th className={`${thClass} text-right`}>SGST</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {report.b2cs.map((row) => (
                  <tr key={`${row.sply_ty}-${row.pos}-${row.rt}`}>
                    <td className={tdClass}>
                      {stateName(row.pos)} ({row.pos})
                    </td>
                    <td className={tdClass}>{row.sply_ty === 'INTER' ? 'Other state' : 'Same state'}</td>
                    <td className={numClass}>{formatNumber(row.rt, 2)}%</td>
                    <td className={numClass}>{formatMoney(row.txval)}</td>
                    <td className={numClass}>{formatMoney(row.iamt)}</td>
                    <td className={numClass}>{formatMoney(row.camt)}</td>
                    <td className={numClass}>{formatMoney(row.samt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportCard>
        )}

        <ReportCard title="HSN summary" description="Quantity and tax per HSN code, rate and unit.">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>HSN</th>
                <th className={thClass}>Description</th>
                <th className={`${thClass} text-right`}>Quantity</th>
                <th className={`${thClass} text-right`}>Rate</th>
                <th className={`${thClass} text-right`}>Taxable Value</th>
                <th className={`${thClass} text-right`}>IGST</th>
                <th className={`${thClass} text-right`}>CGST</th>
                <th className={`${thClass} text-right`}>SGST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {report.hsn.data.length === 0 ? (
                <EmptyRow colSpan={8} text="Nothing was supplied in this period" />
              ) : (
                report.hsn.data.map((row) => (
                  <tr key={`${row.hsn_sc}-${row.rt}-${row.uqc}`}>
                    <td className={`${tdClass} font-mono text-xs`}>{row.hsn_sc}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.desc}</td>
                    <td className={numClass}>
                      {formatNumber(row.qty)} {row.uqc}
                    </td>
                    <td className={numClass}>{formatNumber(row.rt, 2)}%</td>
                    <td className={numClass}>{formatMoney(row.txval)}</td>
                    <td className={numClass}>{formatMoney(row.iamt)}</td>
                    <td className={numClass}>{formatMoney(row.camt)}</td>
                    <td className={numClass}>{formatMoney(row.samt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportCard>

        <ReportCard title="Documents issued" description="The number ranges used in the period, cancelled documents included.">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Document</th>
                <th className={thClass}>From</th>
                <th className={thClass}>To</th>
                <th className={`${thClass} text-right`}>Total</th>
                <th className={`${thClass} text-right`}>Cancelled</th>
                <th className={`${thClass} text-right`}>Net Issued</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {report.doc_issue.doc_det.length === 0 ? (
                <EmptyRow colSpan={6} text="No documents were issued in this period" />
              ) : (
                report.doc_issue.doc_det.flatMap((group) =>
                  group.docs.map((doc) => (
                    <tr key={`${group.doc_num}-${doc.num}`}>
                      <td className={tdClass}>
                        {group.doc_num === 5 ? 'Credit notes' : group.doc_num === 4 ? 'Debit notes' : 'Invoices'}
                      </td>
                      <td className={`${tdClass} font-mono text-xs`}>{doc.from}</td>
                      <td className={`${tdClass} font-mono text-xs`}>{doc.to}</td>
                      <td className={numClass}>{formatNumber(doc.totnum)}</td>
                      <td className={numClass}>{formatNumber(doc.cancel)}</td>
                      <td className={`${numClass} font-medium text-gray-900`}>{formatNumber(doc.net_issue)}</td>
                    </tr>
                  )),
                )
              )}
            </tbody>
          </table>
        </ReportCard>
      </div>
    );
  };

  const renderGSTR3B = () => {
    const report = gstr3b.data;
    if (!report) return null;
    const { summary, sup_details: supplies } = report;
    const rows: { label: string; amounts: TaxAmounts }[] = [
      { label: '(a) Taxable outward supplies', amounts: supplies.osup_det },
      { label: '(b) Zero rated supplies (exports)', amounts: supplies.osup_zero },
      { label: '(c) Nil rated and exempt supplies', amounts: supplies.osup_nil_exmp },
      { label: '(d) Inward supplies under reverse charge', amounts: supplies.isup_rev },
      { label: '(e) Non-GST outward supplies', amounts: supplies.osup_nongst },
    ];
    const estimate = summary.inputTaxEstimate;

    return (
      <div className="space-y-6">
        {renderStats([
          {
            label: 'Taxable Outward Supplies',
            value: formatMoney(supplies.osup_det.txval),
            hint: `Filing period ${formatFilingPeriod(report.fp)}`,
            icon: BanknotesIcon,
            tone: 'text-blue-600 bg-blue-50',
          },
          {
            label: 'Tax on Sales',
            value: formatMoney(summary.outputTax.total),
            hint: `IGST ${formatMoney(summary.outputTax.igst)} · CGST ${formatMoney(summary.outputTax.cgst)} · SGST ${formatMoney(summary.outputTax.sgst)}`,
            icon: ReceiptPercentIcon,
            tone: 'text-teal-600 bg-teal-50',
          },
          {
            label: 'Tax on Purchases (estimate)',
            value: formatMoney(estimate.total),
            hint: `${formatNumber(estimate.purchaseOrders)} received purchase ${estimate.purchaseOrders === 1 ? 'order' : 'orders'}`,
            icon: ClipboardDocumentListIcon,
            tone: 'text-yellow-600 bg-yellow-50',
          },
          {
            label: 'Net Tax (estimate)',
            value: formatMoney(summary.netPayableEstimate),
            hint: 'Tax on sales less tax on purchases',
            icon: CalculatorIcon,
            tone: 'text-green-600 bg-green-50',
          },
        ])}

        <ReportCard title="3.1 Outward supplies and inward supplies under reverse charge">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Nature of Supplies</th>
                <th className={`${thClass} text-right`}>Taxable Value</th>
                <th className={`${thClass} text-right`}>IGST</th>
                <th className={`${thClass} text-right`}>CGST</th>
                <th className={`${thClass} text-right`}>SGST</th>
                <th className={`${thClass} text-right`}>Cess</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((row) => (
                <tr key={row.label}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.label}</td>
                  <td className={numClass}>{formatMoney(row.amounts.txval)}</td>
                  <td className={numClass}>{formatMoney(row.amounts.iamt)}</td>
                  <td className={numClass}>{formatMoney(row.amounts.camt)}</td>
                  <td className={numClass}>{formatMoney(row.amounts.samt)}</td>
                  <td className={numClass}>{formatMoney(row.amounts.csamt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportCard>

        <ReportCard
          title="3.2 Inter-state supplies to unregistered persons"
          description="Taxable sales to customers without a GST number in another state, by place of supply."
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Place of Supply</th>
                <th className={`${thClass} text-right`}>Taxable Value</th>
                <th className={`${thClass} text-right`}>IGST</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {report.inter_sup.unreg_details.length === 0 ? (
                <EmptyRow colSpan={3} text="No such supplies in this period" />
              ) : (
                report.inter_sup.unreg_details.map((row) => (
                  <tr key={row.pos}>
                    <td className={tdClass}>
                      {stateName(row.pos)} ({row.pos})
                    </td>
                    <td className={numClass}>{formatMoney(row.txval)}</td>
                    <td className={numClass}>{formatMoney(row.iamt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportCard>

        <section className="flex gap-3 rounded-lg bg-yellow-50 p-4 text-sm text-yellow-900 ring-1 ring-yellow-600/20">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-yellow-600" />
          <div>
            <p className="font-semibold">4. Input tax credit is not filled in</p>
            <p className="mt-1">
              The credit you can claim is the one shown in GSTR-2B on the GST portal, which this system does not
              hold, so table 4 is left at zero in the exported file. As an indication, received purchase orders of
              this period carry {formatMoney(estimate.total)} of tax (IGST {formatMoney(estimate.igst)}, CGST{' '}
              {formatMoney(estimate.cgst)}, SGST {formatMoney(estimate.sgst)}).
            </p>
          </div>
        </section>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">GST Reports</h1>
          <p className="mt-1 text-sm text-gray-600">GSTR-1 and GSTR-3B figures, built from your issued invoices</p>
        </div>
        <button type="button" onClick={handleExport} disabled={!params || isExporting} className={secondaryButtonClass}>
          <DocumentArrowDownIcon className="mr-2 h-4 w-4" />
          {isExporting ? 'Exporting...' : `Export ${activeTab === 'gstr1' ? 'GSTR-1' : 'GSTR-3B'} JSON`}
        </button>
      </div>

      {/* Parameters */}
      <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Field
            label="GST Number"
            htmlFor="gst-gstin"
            required
            error={gstinError}
            hint={
              selectedGstin
                ? `${selectedGstin.name} · ${formatNumber(selectedGstin.invoices)} invoices`
                : gstins.length === 0 && !gstinsLoading
                  ? 'No invoice has been issued yet; reports are built from issued invoices.'
                  : undefined
            }
          >
            {gstins.length > 0 ? (
              <select id="gst-gstin" value={gstin} onChange={(e) => setGstin(e.target.value)} className={cn(inputClass, 'font-mono')}>
                {gstins.map((entry) => (
                  <option key={entry.gstin} value={entry.gstin}>
                    {entry.gstin}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="gst-gstin"
                type="text"
                maxLength={15}
                value={gstin}
                onChange={(e) => setGstin(e.target.value.toUpperCase().replace(/\s/g, ''))}
                className={cn(inputClass, 'font-mono', gstinError && inputErrorClass)}
                placeholder={gstinsLoading ? 'Loading...' : '27ABCDE1234F1Z5'}
              />
            )}
          </Field>

          <Field label="Period" htmlFor="gst-period">
            <select
              id="gst-period"
              value={preset}
              onChange={(e) => choosePreset(e.target.value as PeriodPreset)}
              className={inputClass}
            >
              {(Object.keys(PERIOD_LABELS) as PeriodPreset[]).map((key) => (
                <option key={key} value={key}>
                  {PERIOD_LABELS[key]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="From" htmlFor="gst-from" required>
            <input
              id="gst-from"
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPreset('custom');
              }}
              className={inputClass}
            />
          </Field>

          <Field label="To" htmlFor="gst-to" required error={dateError}>
            <input
              id="gst-to"
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => {
                setToDate(e.target.value);
                setPreset('custom');
              }}
              className={cn(inputClass, dateError && inputErrorClass)}
            />
          </Field>
        </div>
      </div>

      {/* Report tabs */}
      <div className="inline-flex rounded-lg bg-gray-100 p-1" role="tablist">
        {(
          [
            { key: 'gstr1', label: 'GSTR-1', hint: 'Outward supplies' },
            { key: 'gstr3b', label: 'GSTR-3B', hint: 'Monthly summary' },
          ] as { key: ReportTab; label: string; hint: string }[]
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-semibold',
              activeTab === tab.key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-600 hover:text-gray-900',
            )}
          >
            {tab.label}
            <span className="ml-2 text-xs font-normal text-gray-500">{tab.hint}</span>
          </button>
        ))}
      </div>

      {/* Report */}
      {!params ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center shadow-sm ring-1 ring-gray-200">
          <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-2 text-sm font-medium text-gray-900">Choose a GST number and a period</p>
          <p className="mt-1 text-sm text-gray-500">The report is worked out as soon as both are set.</p>
        </div>
      ) : active.isLoading ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200">
          Working out the report...
        </div>
      ) : active.error ? (
        <div className="rounded-lg bg-white px-4 py-12 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-red-600">{getApiErrorMessage(active.error, 'Could not work out the report')}</p>
        </div>
      ) : (
        <div className={active.isFetching ? 'opacity-60' : ''}>{activeTab === 'gstr1' ? renderGSTR1() : renderGSTR3B()}</div>
      )}

      {/* Invoices behind the return */}
      {params && (
        <section className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Invoices in this period</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Issued and paid documents under {params.gstin}. Drafts and cancelled invoices are not reported.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>Invoice</th>
                  <th className={thClass}>Date</th>
                  <th className={thClass}>Customer</th>
                  <th className={thClass}>Place of Supply</th>
                  <th className={`${thClass} text-right`}>Taxable Value</th>
                  <th className={`${thClass} text-right`}>Tax</th>
                  <th className={`${thClass} text-right`}>Invoice Value</th>
                  <th className={thClass}>Status</th>
                </tr>
              </thead>
              <tbody
                className={`divide-y divide-gray-100 bg-white ${register.isFetching && !register.isLoading ? 'opacity-60' : ''}`}
              >
                {register.isLoading ? (
                  <EmptyRow colSpan={8} text="Loading invoices..." />
                ) : registerRows.length === 0 ? (
                  <EmptyRow colSpan={8} text="No issued invoices in this period" />
                ) : (
                  registerRows.map((invoice) => {
                    const status = getEffectiveStatus(invoice);
                    return (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                            {invoice.invoiceNumber}
                          </span>
                          <div className="mt-1 text-xs text-gray-500">{INVOICE_TYPE_LABELS[invoice.invoiceType]}</div>
                        </td>
                        <td className={tdClass}>{formatInvoiceDate(invoice.invoiceDate)}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{invoice.customerName}</div>
                          <div className="font-mono text-xs text-gray-500">{invoice.customerGSTIN || 'Unregistered'}</div>
                        </td>
                        <td className={tdClass}>
                          <div>{invoice.placeOfSupply}</div>
                          <div className="text-xs text-gray-500">{GST_TYPE_LABELS[invoice.gstType]}</div>
                        </td>
                        <td className={numClass}>{formatMoney(invoice.totalTaxableAmount)}</td>
                        <td className={numClass}>
                          {formatMoney(invoice.totalCGST + invoice.totalSGST + invoice.totalIGST + invoice.totalCess)}
                        </td>
                        <td className={`${numClass} font-medium text-gray-900`}>{formatMoney(invoice.grandTotal)}</td>
                        <td className={tdClass}>
                          <span className={`${badgeClass} ${INVOICE_STATUS_META[status].color}`}>
                            {INVOICE_STATUS_META[status].label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {registerTotal > 0 && (
            <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
                <span>
                  Showing {formatNumber((page - 1) * pageSize + 1)}–
                  {formatNumber(Math.min(page * pageSize, registerTotal))} of {formatNumber(registerTotal)} invoices
                </span>
                <label className="flex items-center gap-2">
                  Rows per page
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <Pagination className="lg:flex-1" currentPage={page} totalPages={registerPages} onPageChange={setPage} />
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default GSTReports;
